import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useCustomAlertStore } from "../Store/store";
import RenderMemberCard from "../components/userManagement/RenderMemberCard";
import {
  ChangeCompanyStatus,
  ChangeMemberStatus,
  getAdminData,
} from "../api/userManagementAPI";
import MemberTable from "../components/userManagement/MemberTable";
import RenderCompanyCard from "../components/userManagement/RenderCompanyCard";
import CompanyTable from "../components/userManagement/CompanyTable";

const AdminPageContainer = styled.div`
  width: 100%;
  height: 100vh;
  background: radial-gradient(
    circle at center,
    #202720 0%,
    #202720 30%,
    #252525 50%,
    #1c1c1c 100%
  );
  color: #fff;
  overflow-y: auto;
  padding: 80px 0;
`;

const AdminHeader = styled.header`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);

  h1 {
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 10px;
    color: #fff;
  }

  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
  }
`;

const AdminTabs = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 0;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
    padding: 5px;
  }

  li {
    flex: 1;
  }

  button {
    width: 100%;
    padding: 15px 20px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }

    &.active {
      background: #4caf50;
      color: #fff;
      font-weight: 600;
    }
  }
`;

const AdminContent = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const ContentSection = styled.section`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  h2 {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 30px;
    color: #fff;
  }
`;

const ApprovalCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-top: 20px;

  &.table-view {
    grid-template-columns: 1fr;
  }
`;

const UserCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 25px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
  }

  .user-info {
    margin-bottom: 20px;

    .user-name {
      font-size: 18px;
      font-weight: 600;
      color: #fff;
      margin-bottom: 8px;
    }

    .user-email {
      color: rgba(255, 255, 255, 0.7);
      font-size: 14px;
      margin-bottom: 5px;
    }

    .user-company {
      color: #4caf50;
      font-size: 14px;
      font-weight: 500;
    }

    .user-status {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
      margin-top: 10px;

      &.pending {
        background: rgba(255, 193, 7, 0.2);
        color: #ffc107;
        border: 1px solid rgba(255, 193, 7, 0.3);
      }

      &.approved {
        background: rgba(76, 175, 80, 0.2);
        color: #4caf50;
        border: 1px solid rgba(76, 175, 80, 0.3);
      }

      &.rejected {
        background: rgba(244, 67, 54, 0.2);
        color: #f44336;
        border: 1px solid rgba(244, 67, 54, 0.3);
      }
    }
  }

  .card-actions {
    display: flex;
    gap: 10px;

    button {
      flex: 1;
      padding: 10px 15px;
      border: none;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.3s ease;

      &.approve {
        background: #4caf50;
        color: #fff;

        &:hover {
          background: #45a049;
        }
      }

      &.reject {
        background: #f44336;
        color: #fff;

        &:hover {
          background: #da190b;
        }
      }

      &.edit {
        background: rgba(255, 255, 255, 0.1);
        color: #fff;
        border: 1px solid rgba(255, 255, 255, 0.2);

        &:hover {
          background: rgba(255, 255, 255, 0.2);
        }
      }
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.5);

  .empty-icon {
    font-size: 48px;
    margin-bottom: 20px;
  }

  .empty-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  .empty-description {
    font-size: 14px;
  }
`;

export default function UserManagementPage() {
  const [activeTab, setActiveTab] = useState("member-approval");
  const { showSuccessAlert, showErrorAlert } = useCustomAlertStore();

  const tabs = [
    { id: "member-approval", label: "회원 승인" },
    { id: "company-approval", label: "기업등록 승인" },
    { id: "member-management", label: "회원 관리" },
    { id: "company-management", label: "기업 관리" },
  ];

  const [memberData, setMemberData] = useState([]);
  const [companyData, setCompanyData] = useState([]);
  const [memberManagementData, setMemberManagementData] = useState([]);
  const [companyManagementData, setCompanyManagementData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // 각 탭별 데이터 리로드용 boolean 상태
  const [reloadMemberData, setReloadMemberData] = useState(false);
  const [reloadCompanyData, setReloadCompanyData] = useState(false);
  const [reloadMemberManagementData, setReloadMemberManagementData] =
    useState(false);
  const [reloadCompanyManagementData, setReloadCompanyManagementData] =
    useState(false);
  const fetchData = async () => {
    setIsLoading(true);
    try {
      // TODO: API 호출 로직 추가
      const response = await getAdminData(activeTab);

      // 임시 데이터
      // const mockData = generateMockData(activeTab);

      if (activeTab === "member-approval") {
        setMemberData(response);
        setReloadMemberData(false);
      } else if (activeTab === "company-approval") {
        setCompanyData(response);
        setReloadCompanyData(false);
      } else if (activeTab === "member-management") {
        setMemberManagementData(response);
        setReloadMemberManagementData(false);
      } else if (activeTab === "company-management") {
        setCompanyManagementData(response);
        setReloadCompanyManagementData(false);
      }
    } catch (error) {
      console.error("데이터 로드 실패:", error);
      showErrorAlert("데이터 로드 실패", "데이터를 불러오는데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    activeTab,
    reloadMemberData,
    reloadCompanyData,
    reloadMemberManagementData,
    reloadCompanyManagementData,
  ]);

  // 임시 목 데이터 생성 함수
  // const generateMockData = (tabId) => {
  //   if (tabId === "member-approval") {
  //     return [
  //       {
  //         id: 1,
  //         name: "홍길동",
  //         email: "hong@example.com",
  //         company: "농업회사 한국",
  //         status: "pending",
  //         createdAt: "2024-01-15",
  //       },
  //       {
  //         id: 2,
  //         name: "김철수",
  //         email: "kim@example.com",
  //         company: "스마트팜 코리아",
  //         status: "pending",
  //         createdAt: "2024-01-16",
  //       },
  //     ];
  //   } else if (tabId === "company-approval") {
  //     return [
  //       {
  //         id: 1,
  //         companyName: "농업기술 주식회사",
  //         businessNumber: "123-45-67890",
  //         email: "info@agritech.com",
  //         status: "pending",
  //         createdAt: "2024-01-15",
  //       },
  //     ];
  //   }
  //   return [];
  // };

  // 각 탭별 데이터 리로드 함수들
  const handleReloadMemberData = () => setReloadMemberData(true);
  const handleReloadCompanyData = () => setReloadCompanyData(true);
  const handleReloadMemberManagementData = () =>
    setReloadMemberManagementData(true);
  const handleReloadCompanyManagementData = () =>
    setReloadCompanyManagementData(true);

  // 승인/거절 처리 함수
  const handleApproval = async (userName, action, type) => {
    try {
      // TODO: API 호출 로직 추가

      // 데이터 리로드
      if (type === "member") {
        ChangeMemberStatus(userName, action);
        showSuccessAlert(
          "처리 완료",
          `${action === 1 ? "승인" : "거절"}이 완료되었습니다.`
        );
        handleReloadMemberData();
      } else {
        ChangeCompanyStatus(userName, action);
        showSuccessAlert(
          "처리 완료",
          `${action === 1 ? "승인" : "거절"}이 완료되었습니다.`
        );
        handleReloadCompanyData();
      }
    } catch (error) {
      console.error("처리 실패:", error);
      showErrorAlert("처리 실패", "요청 처리에 실패했습니다.");
    }
  };

  // const renderCompanyCard = (company) => (
  //   <UserCard key={company.id}>
  //     <div className="user-info">
  //       <div className="user-name">{company.companyName}</div>
  //       <div className="user-email">{company.email}</div>
  //       <div className="user-company">사업자번호: {company.businessNumber}</div>
  //       <span className={`user-status ${company.status}`}>
  //         {company.status === "pending"
  //           ? "승인 대기"
  //           : company.status === "approved"
  //           ? "승인됨"
  //           : "거절됨"}
  //       </span>
  //     </div>
  //     {company.status === "pending" && (
  //       <div className="card-actions">
  //         <button
  //           className="approve"
  //           onClick={() => handleApproval(company.id, "approve", "company")}
  //         >
  //           승인
  //         </button>
  //         <button
  //           className="reject"
  //           onClick={() => handleApproval(company.id, "reject", "company")}
  //         >
  //           거절
  //         </button>
  //       </div>
  //     )}
  //   </UserCard>
  // );

  const renderEmptyState = (message) => (
    <EmptyState>
      <div className="empty-icon">📋</div>
      <div className="empty-title">데이터가 없습니다</div>
      <div className="empty-description">{message}</div>
    </EmptyState>
  );

  return (
    <AdminPageContainer>
      <AdminHeader>
        <h1>사용자 관리</h1>
        <p>
          회원 승인과 기업 등록 승인, 그리고 사용자 관리를 수행할 수 있습니다.
        </p>
      </AdminHeader>

      <AdminTabs>
        <ul>
          {tabs.map((tab) => (
            <li key={tab.id}>
              <button
                className={activeTab === tab.id ? "active" : ""}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </AdminTabs>

      <AdminContent>
        <ContentSection>
          <h2>{tabs.find((tab) => tab.id === activeTab)?.label}</h2>

          <ApprovalCards
            className={
              activeTab === "member-management" ||
              activeTab === "company-management"
                ? "table-view"
                : ""
            }
          >
            {isLoading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px",
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                로딩중...
              </div>
            ) : (
              <>
                {activeTab === "member-approval" &&
                  (memberData.length > 0
                    ? memberData.map((member, index) => (
                        <RenderMemberCard
                          member={member}
                          handleApproval={handleApproval}
                          key={index}
                        />
                      ))
                    : renderEmptyState("승인 대기 중인 회원이 없습니다."))}

                {activeTab === "company-approval" &&
                  (companyData.length > 0
                    ? companyData.map((company, index) => (
                        <RenderCompanyCard
                          company={company}
                          handleApproval={handleApproval}
                          key={index}
                        />
                      ))
                    : renderEmptyState("승인 대기 중인 기업이 없습니다."))}

                {activeTab === "member-management" &&
                  (memberManagementData.length > 0 ? (
                    <MemberTable
                      memberManagementData={memberManagementData}
                      onDataChange={handleReloadMemberManagementData}
                    />
                  ) : (
                    renderEmptyState("관리중인 회원이 없습니다.")
                  ))}

                {activeTab === "company-management" &&
                  (companyManagementData.length > 0 ? (
                    <CompanyTable
                      companyManagementData={companyManagementData}
                      onDataChange={handleReloadCompanyManagementData}
                    />
                  ) : (
                    renderEmptyState("관리중인 기업이 없습니다.")
                  ))}
              </>
            )}
          </ApprovalCards>
        </ContentSection>
      </AdminContent>
    </AdminPageContainer>
  );
}
